import React, { useEffect, useState, useRef } from "react";
import HoldButton from "../helpers/holdButton";
import Debugger from "../helpers/debugger";
import DebuggerGUI from "../helpers/GUI/DebuggerGUIProps";

import gsap from "gsap";
import HandDetection from "../helpers/detection2"; // this loads the new model
import DebuggerCamera from "../helpers/GUI/DebuggerCamera";
import { Options } from "../../types";

import {
  GlobalMode,
  GlobalModeValues,
  getDefaultMode,
  saveMode,
} from "../helpers/mode";
import powerUpVerification from "../helpers/powerUpVerification";
import {
  getParsedHashtableProfiles,
  setHasTableProfilesStorage,
} from "../helpers/disabledProfile";
import { getCurrentVideo, getRunExperience } from "../helpers/screensaverVideo";
import { useNavigate } from "react-router";

import { useUsermaven } from "@usermaven/react";
import { usePageView } from "@usermaven/react";

interface ScreenSaverProps {
  triggerTransition: (to: string) => void;
}

const IMG_1 = "./assets/imgs/Intro1.png?v=1.0.1";
const IMG_2 = "./assets/imgs/Intro2.png?v=1.0.1";

const ScreenSaver: React.FC<ScreenSaverProps> = ({ triggerTransition }) => {
  usePageView();
  const { track } = useUsermaven();
  let navigate = useNavigate();

  const [showDebugger, setShowDebugger] = useState<boolean>(false);

  // Create global variable for modes "GESTURE" or "TOUCH"
  const [usingMode, setUsingMode] = useState<GlobalMode>(getDefaultMode());

  const [currentVideo, setCurrentVideo] = useState("2026.mp4");
  const img1Ref = useRef<HTMLImageElement>(null);
  const img2Ref = useRef<HTMLImageElement>(null);

  const [hashTableProfiles, setHashTableProfiles] = useState(
    getParsedHashtableProfiles(),
  );

  const savedOptions = localStorage.getItem("camera_options") as string;
  const parsedOptions = savedOptions ? JSON.parse(savedOptions) : null;
  let [options, setOptions] = useState<Options>(
    parsedOptions
      ? parsedOptions
      : {
          maxNumHands: 1, // n
          modelComplexity: 1, // 0 - 1
          minDetectionConfidence: 0.5, // 0.0 - 1.0
          minTrackingConfidence: 0.8, // 0.0 - 1.0
        },
  );

  const [runTouchExperience, setRunTouchExperience] = useState(
    getRunExperience() || "true",
  );

  const [signDetected, setSignDetected] = useState(false);
  const detectionRef = useRef<boolean | null>(null);
  const holdTimer = useRef<NodeJS.Timeout | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [signHeld, setSignHeld] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const isHoldingRef = useRef(isHolding);
  const [intervalRef, setIntervalRef] = useState<NodeJS.Timeout | null>(null);
  const intervalRewind = useRef<NodeJS.Timeout | null>(null);

  const [secondsHolder, setSecondsHolder] = useState<number | null>(null);

  const changeMode = (mode: GlobalMode) => {
    setUsingMode(mode);
    saveMode(mode); // Guardamos el nuevo modo
  };

  useEffect(() => {
    if (signDetected) {
      holdTimer.current = setTimeout(() => {
        setSignHeld(true);
      }, 500);
    } else {
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
        holdTimer.current = null;
      }
    }

    return () => {
      if (holdTimer.current) clearTimeout(holdTimer.current);
    };
  }, [signDetected]);

  useEffect(() => {
    detectionRef.current = signDetected;
  }, [signDetected]);

  useEffect(() => {
    isHoldingRef.current = isHolding;
  }, [isHolding]);

  useEffect(() => {
    if (videoRef.current && signHeld) {
      powerUpVerification(
        videoRef as React.RefObject<HTMLVideoElement>,
        signHeld,
        setSignHeld,
        detectionRef,
        intervalRef,
        setIntervalRef,
        intervalRewind,
        usingMode,
        isHoldingRef,
      );
    }
  }, [detectionRef, signHeld, isHoldingRef]); // eslint-disable-line

  useEffect(() => {
    gsap.set([img1Ref.current, img2Ref.current], {
      opacity: 0,
      scale: 1,
    });
    const tl = gsap.timeline({ repeat: -1 });

    const duration1 = 60;
    const duration2 = 30;
    const transition = 1.5;

    tl.fromTo(
      img1Ref.current,
      { opacity: 0, scale: 0.9 },
      { opacity: 1, scale: 1, duration: 1 },
    )
      .to(
        img1Ref.current,
        {
          opacity: 0,
          scale: 0.9,
          duration: transition,
          ease: "power1.out",
        },
        `+=${duration1}`,
      )

      .set(img1Ref.current, { scale: 1 })

      .fromTo(
        img2Ref.current,
        { opacity: 0, scale: 0.9 },
        { opacity: 1, scale: 1, duration: 1 },
        `<`,
      )
      .to(
        img2Ref.current,
        {
          opacity: 0,
          scale: 0.9,
          duration: transition,
          ease: "power1.out",
        },
        `+=${duration2}`,
      )

      .set(img2Ref.current, { scale: 1 });

    return () => {
      tl.kill();
    };
  }, []);

  const changeCameraParameters = (parameter: string, value: string) => {
    const parsedValue = parseFloat(value);

    setOptions((prevOptions) => ({
      ...prevOptions,
      [parameter]: isNaN(parsedValue) ? value : parsedValue,
    }));
  };

  interface ButtonsChangeValueParams {
    sum: boolean;
    value: number | string | undefined;
    parameter: string;
    step: number;
    min: number;
    max: number;
  }

  const buttonsChangeValue = ({
    sum,
    value,
    parameter,
    step,
    min,
    max,
  }: ButtonsChangeValueParams) => {
    console.log("value, current", { sum, value, parameter, step, min, max });

    if (value === undefined) return;

    let newValue = sum ? Number(value) + step : Number(value) - step;

    if (newValue < min) newValue = min;
    if (newValue > max) newValue = max;

    console.log("newValue", newValue);

    changeCameraParameters(parameter, newValue.toString());
  };

  useEffect(() => {
    localStorage.setItem("camera_options", JSON.stringify(options));
  }, [options]);

  const handleTouchExperience = () => {
    track("Start Experience", {});
  };

  return (
    <div id="screensaver" className="h-screen">
      <Debugger setShowDebugger={setShowDebugger} showDebugger={showDebugger} />

      <DebuggerGUI
        showDebugger={showDebugger}
        setShowDebugger={setShowDebugger}
        setHashTableProfiles={setHashTableProfiles}
        hashTableProfiles={hashTableProfiles}
      />

      <div
        className="h-screen bg-white"
        onClick={() => {
          runTouchExperience === "true" && navigate("/menu");
        }}
      >
        <div className="w-full h-full bg-[#021220] fixed z-10 opacity-40"></div>
        <video
          autoPlay
          playsInline
          loop
          muted
          className={`h-full w-full object-cover pointer-events-none fixed `}
        >
          <source
            src={`${process.env.PUBLIC_URL}/assets/screensavers/${currentVideo}`}
          />
        </video>

        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <img
            ref={img1Ref}
            src={IMG_1}
            alt="Screen 1"
            className="absolute w-full h-full object-contain will-change-transform"
          />
          <img
            ref={img2Ref}
            src={IMG_2}
            alt="Screen 2"
            className="absolute w-full h-full object-contain will-change-transform"
          />
        </div>
      </div>
    </div>
  );
};

export default ScreenSaver;
